// src/admin/pages/AdminShipping.js
import React, { useState, useEffect, useCallback } from 'react';
import { showToast } from '../../contexts/ToastNotification';
import axios from 'axios';
import './admin-shipping.scss';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function AdminShipping() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [trackingData, setTrackingData] = useState({
        carrier: '',
        trackingNumber: '',
        estimatedDelivery: ''
    });
    const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 1 });
    const [filters, setFilters] = useState({ status: 'processing', search: '' });
    const [updating, setUpdating] = useState(null);

    // Corrieri disponibili
    const carriers = [
        { value: 'brt', label: '📦 BRT Bartolini', logo: 'brt', trackingUrl: 'https://www.brt.it/track?trackId=' },
        { value: 'sda', label: '📮 SDA Express', logo: 'sda', trackingUrl: 'https://www.sda.it/tracking?trackId=' },
        { value: 'dhl', label: '✈️ DHL Express', logo: 'dhl', trackingUrl: 'https://www.dhl.com/it-en/home/tracking.html?tracking-id=' },
        { value: 'gls', label: '🚚 GLS Italia', logo: 'gls', trackingUrl: 'https://www.gls-italy.com/tracking?trackId=' },
        { value: 'poste', label: '📬 Poste Italiane', logo: 'poste', trackingUrl: 'https://www.poste.it/online/dovequando/home.html?codice=' }
    ];

    // Stati spedizione
    const shippingStatuses = [
        { value: 'pending', label: '📝 In attesa', color: '#ffc107', bg: '#fff3cd' },
        { value: 'processing', label: '⚙️ In preparazione', color: '#17a2b8', bg: '#d1ecf1' },
        { value: 'shipped', label: '🚚 In transito', color: '#007bff', bg: '#cce5ff' },
        { value: 'delivered', label: '✅ Consegnato', color: '#28a745', bg: '#d4edda' },
        { value: 'delayed', label: '⏰ Ritardato', color: '#fd7e14', bg: '#ffe5d0' }
    ];

    // Carica ordini da spedire
    const loadOrders = useCallback(async () => {
        setLoading(true);
        try {
            let url = `${API_URL}/admin/orders?page=${pagination.page}&limit=15&status=${filters.status}`;
            if (filters.search) url += `&search=${encodeURIComponent(filters.search)}`;
            
            // ✅ Convertito a axios
            const response = await axios.get(url, { withCredentials: true });
            const data = response.data;
            
            if (data.success) {
                setOrders(data.orders);
                setPagination(prev => ({
                    ...prev,
                    total: data.pagination.total,
                    totalPages: data.pagination.totalPages
                }));
            } else {
                showToast('Errore nel caricamento degli ordini', 'error');
            }
        } catch (error) {
            console.error('Error loading orders:', error);
            showToast('Errore di connessione', 'error');
        } finally {
            setLoading(false);
        }
    }, [pagination.page, filters.status, filters.search]);

    useEffect(() => {
        loadOrders();
    }, [loadOrders]);

    // Aggiungi tracking
    const addTracking = async (orderId) => {
        if (!trackingData.carrier || !trackingData.trackingNumber) {
            showToast('Seleziona un corriere e inserisci il numero di tracking', 'warning');
            return;
        }

        setUpdating(orderId);
        try {
            // ✅ Convertito a axios
            const response = await axios.put(
                `${API_URL}/admin/orders/${orderId}/tracking`,
                {
                    carrier: trackingData.carrier,
                    trackingNumber: trackingData.trackingNumber,
                    estimatedDelivery: trackingData.estimatedDelivery
                },
                { withCredentials: true }
            );
            
            const data = response.data;
            
            if (data.success) {
                showToast(`Tracking aggiunto con successo per l'ordine #${orderId}`, 'success');
                setSelectedOrder(null);
                setTrackingData({ carrier: '', trackingNumber: '', estimatedDelivery: '' });
                loadOrders();
            } else {
                showToast(data.message || 'Errore nell\'aggiunta del tracking', 'error');
            }
        } catch (error) {
            console.error('Error adding tracking:', error);
            showToast('Errore di connessione', 'error');
        } finally {
            setUpdating(null);
        }
    };

    // Genera numero tracking casuale (per demo)
    const generateTrackingNumber = (carrier) => {
        const prefix = {
            brt: 'BRT',
            sda: 'SDA',
            dhl: 'DHL',
            gls: 'GLS',
            poste: 'PN'
        };
        const randomNum = Math.random().toString(36).substring(2, 15).toUpperCase();
        return `${prefix[carrier] || 'TRK'}-${randomNum}`;
    };

    // Formatta data
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('it-IT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    // Ottieni stato formattato
    const getStatusDisplay = (status) => {
        const statusConfig = shippingStatuses.find(s => s.value === status);
        return statusConfig || { label: status, color: '#6c757d', bg: '#e9ecef' };
    };

    // Apri modal tracking
    const openTrackingModal = (order) => {
        setSelectedOrder(order);
        setTrackingData({
            carrier: '',
            trackingNumber: '',
            estimatedDelivery: ''
        });
    };

    if (loading && orders.length === 0) {
        return (
            <div className="admin-loading">
                <div className="loader"></div>
                <span>Caricamento ordini...</span>
            </div>
        );
    }

    return (
        <div className="admin-shipping">
            {/* Header */}
            <div className="shipping-header">
                <div className="header-title">
                    <h2>🚚 Tracking Spedizioni</h2>
                    <p>Gestisci le spedizioni e monitora i pacchi in consegna</p>
                </div>
                <div className="shipping-filters">
                    <div className="search-wrapper">
                        <span className="search-icon">🔍</span>
                        <input
                            type="text"
                            placeholder="Cerca per numero ordine o cliente..."
                            value={filters.search}
                            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }))}
                            className="search-input"
                        />
                    </div>
                    <select
                        value={filters.status}
                        onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value, page: 1 }))}
                        className="status-filter"
                    >
                        {shippingStatuses.map(s => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="shipping-stats">
                <div className="stat-card">
                    <div className="stat-icon">⏳</div>
                    <div className="stat-value">{orders.filter(o => o.stato === 'processing').length}</div>
                    <div className="stat-label">Da spedire</div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">🚚</div>
                    <div className="stat-value">{orders.filter(o => o.stato === 'shipped').length}</div>
                    <div className="stat-label">In transito</div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">✅</div>
                    <div className="stat-value">{orders.filter(o => o.stato === 'delivered').length}</div>
                    <div className="stat-label">Consegnati</div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">⏰</div>
                    <div className="stat-value">{orders.filter(o => o.stato === 'delayed').length}</div>
                    <div className="stat-label">Ritardati</div>
                </div>
            </div>

            {/* Orders Table */}
            <div className="shipping-table-container">
                <table className="shipping-table">
                    <thead>
                        <tr>
                            <th>ID Ordine</th>
                            <th>Cliente</th>
                            <th>Data ordine</th>
                            <th>Totale</th>
                            <th>Indirizzo</th>
                            <th>Stato</th>
                            <th>Tracking</th>
                            <th>Azioni</th>
                        </tr>
                    </thead>
                    <tbody>
                        {orders.map(order => {
                            const statusDisplay = getStatusDisplay(order.stato);
                            const hasTracking = order.trackingNumber;
                            
                            return (
                                <tr key={order.id}>
                                    <td className="order-number">#{order.numero_ordine || order.id}</td>
                                    <td>
                                        <div className="customer-info">
                                            <strong>{order.cliente.nome} {order.cliente.cognome}</strong>
                                            <span>{order.cliente.email}</span>
                                        </div>
                                    </td>
                                    <td>{formatDate(order.data_creazione)}</td>
                                    <td className="order-total">€ {order.totale?.toFixed(2) || '0.00'}</td>
                                    <td>
                                        <div className="address-info">
                                            {order.indirizzo_spedizione?.indirizzo || 'N/D'}
                                        </div>
                                    </td>
                                    <td>
                                        <span 
                                            className="status-badge"
                                            style={{ backgroundColor: statusDisplay.bg, color: statusDisplay.color }}
                                        >
                                            {statusDisplay.label}
                                        </span>
                                    </td>
                                    <td>
                                        {hasTracking ? (
                                            <div className="tracking-info">
                                                <span className="tracking-number">{order.trackingNumber}</span>
                                                <span className="tracking-carrier">{order.carrier}</span>
                                            </div>
                                        ) : (
                                            <span className="no-tracking">❌ Non assegnato</span>
                                        )}
                                    </td>
                                    <td>
                                        {order.stato === 'processing' ? (
                                            <button 
                                                className="add-tracking-btn"
                                                onClick={() => openTrackingModal(order)}
                                                disabled={updating === order.id}
                                            >
                                                ✏️ Aggiungi Tracking
                                            </button>
                                        ) : order.stato === 'shipped' ? (
                                            <a 
                                                href={order.trackingUrl} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="track-link"
                                            >
                                                🔗 Traccia
                                            </a>
                                        ) : order.stato === 'delivered' ? (
                                            <span className="delivered-badge">✅ Consegnato</span>
                                        ) : null}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* No Results */}
            {orders.length === 0 && !loading && (
                <div className="no-results">
                    <div className="no-results-icon">🚚</div>
                    <p>Nessun ordine trovato</p>
                </div>
            )}

            {/* Pagination */}
            {pagination.totalPages > 1 && (
                <div className="pagination">
                    <button 
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                        disabled={pagination.page === 1}
                        className="page-btn"
                    >
                        ← Precedente
                    </button>
                    <div className="page-info">
                        Pagina {pagination.page} di {pagination.totalPages}
                    </div>
                    <button 
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                        disabled={pagination.page === pagination.totalPages}
                        className="page-btn"
                    >
                        Successiva →
                    </button>
                </div>
            )}

            {/* Tracking Modal */}
            {selectedOrder && (
                <div className="modal-overlay" onClick={() => setSelectedOrder(null)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>🚚 Aggiungi Tracking</h3>
                            <button className="close-modal" onClick={() => setSelectedOrder(null)}>✕</button>
                        </div>
                        <div className="modal-body">
                            <div className="order-summary">
                                <p><strong>Ordine:</strong> #{selectedOrder.numero_ordine || selectedOrder.id}</p>
                                <p><strong>Cliente:</strong> {selectedOrder.cliente.nome} {selectedOrder.cliente.cognome}</p>
                                <p><strong>Indirizzo:</strong> {selectedOrder.indirizzo_spedizione?.indirizzo || 'N/D'}</p>
                            </div>
                            <div className="form-group">
                                <label>Corriere *</label>
                                <div className="carrier-buttons">
                                    {carriers.map(carrier => (
                                        <button
                                            key={carrier.value}
                                            type="button"
                                            className={`carrier-btn ${trackingData.carrier === carrier.value ? 'active' : ''}`}
                                            onClick={() => {
                                                setTrackingData({
                                                    ...trackingData,
                                                    carrier: carrier.value,
                                                    trackingNumber: generateTrackingNumber(carrier.value)
                                                });
                                            }}
                                        >
                                            {carrier.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Numero di Tracking *</label>
                                <input
                                    type="text"
                                    value={trackingData.trackingNumber}
                                    onChange={(e) => setTrackingData({ ...trackingData, trackingNumber: e.target.value })}
                                    placeholder="Inserisci il numero di tracking"
                                    className="tracking-input"
                                />
                            </div>
                            <div className="form-group">
                                <label>Data consegna stimata</label>
                                <input
                                    type="date"
                                    value={trackingData.estimatedDelivery}
                                    onChange={(e) => setTrackingData({ ...trackingData, estimatedDelivery: e.target.value })}
                                    className="date-input"
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="cancel-btn" onClick={() => setSelectedOrder(null)}>
                                Annulla
                            </button>
                            <button 
                                className="save-btn"
                                onClick={() => addTracking(selectedOrder.id)}
                                disabled={updating === selectedOrder.id}
                            >
                                {updating === selectedOrder.id ? 'Salvataggio...' : '💾 Salva Tracking'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminShipping;